import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";
import { PROJECT_DETAIL } from "@/constants/testIds";
import { Reveal } from "@/components/Reveal";
import { Counter } from "@/components/Counter";

function getMetricsGridClass(count) {
  if (count === 3) return "md:grid-cols-3";
  if (count === 2) return "md:grid-cols-2";
  return "md:grid-cols-4";
}

export default function ProgettoDetail() {
  const { slug } = useParams();
  const [project, setProject] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    setProject(null);
    setError(null);
    api
      .get(`/projects/${slug}`)
      .then((r) => setProject(r.data))
      .catch(() => setError("Progetto non trovato."));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen bg-cream pt-32 px-6 text-center">
        <h1 className="font-heading text-5xl mb-6">404</h1>
        <p className="text-ink/70 mb-8">{error}</p>
        <Link to="/progetti" className="editorial-link">← Tutti i progetti</Link>
      </div>
    );
  }

  if (!project) return <div className="min-h-screen bg-cream" />;

  return (
    <div data-testid={PROJECT_DETAIL.page} className="bg-cream grain pt-32 pb-24">
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16">
        <Link
          to="/progetti"
          data-testid={PROJECT_DETAIL.backLink}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] editorial-link mb-12"
        >
          <ArrowLeft size={14} /> Indice progetti
        </Link>

        <div className="grid grid-cols-12 gap-6 items-end">
          <div className="col-span-12 md:col-span-2">
            <span className="num-index text-7xl md:text-9xl text-peach leading-none">
              {String(project.index).padStart(2, "0")}
            </span>
          </div>
          <div className="col-span-12 md:col-span-10">
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-4">
              {project.context} · {project.year}
            </p>
            <Reveal>
              <h1 className="font-heading text-6xl md:text-[8rem] leading-[0.85] tracking-tighter">
                {project.title}
              </h1>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-12 gap-6 mt-16 md:mt-24">
          <div className="md:col-span-5 col-span-12">
            <Reveal>
              <p className="font-heading italic text-2xl md:text-3xl text-ink/80 leading-tight">
                {project.tagline}
              </p>
            </Reveal>
          </div>
          <div className="md:col-span-6 md:col-start-7 col-span-12">
            <Reveal delay={0.1}>
              <p className="text-ink/80 leading-relaxed text-lg">{project.description}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* COVER IMAGE / ANCHOR NAV (UQIDO uses anchor nav; Biasetto/Alajmo/Liquid Diamond skip cover) */}
      {(() => {
        if (project.slug === "uqido") {
          return (
            <section
              data-testid="uqido-anchor-nav"
              className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-20 md:mt-24"
            >
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {[
                  { label: "ChatDoc", href: "#section-chatdoc" },
                  { label: "Beyond the Hype", href: "#section-beyond-the-hype" },
                  { label: "Masterclass AI for Business", href: "#section-masterclass-ai-for-business" },
                  { label: "Personal Branding", href: "#section-personal-branding" },
                ].map((b) => (
                  <a
                    key={b.href}
                    href={b.href}
                    data-testid={`uqido-anchor-${b.href.slice(1)}`}
                    className="group inline-flex items-center justify-center text-center rounded-full border border-line-dark px-4 py-3 text-[10px] md:text-xs uppercase tracking-[0.18em] text-ink/80 hover:border-peach hover:text-peach transition-colors"
                  >
                    {b.label}
                  </a>
                ))}
              </div>
            </section>
          );
        }
        if (["atelier-biasetto", "alajmo", "liquid-diamond"].includes(project.slug)) {
          return null;
        }
        return (
          <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-20 md:mt-24">
            <Reveal>
              <div className="aspect-[16/9] overflow-hidden bg-cream-alt">
                <img
                  src={project.cover_image}
                  alt={project.title}
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
            </Reveal>
          </section>
        );
      })()}

      {/* METRICS */}
      {project.metrics?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16">
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-12">— Risultati</p>
          <div className={`grid grid-cols-2 gap-px bg-line ${getMetricsGridClass(project.metrics.length)}`}>
            {project.metrics.map((m) => (
              <div
                key={m.label}
                data-testid={PROJECT_DETAIL.metric(m.label.toLowerCase().replace(/\s+/g, "-"))}
                className="bg-cream p-8 md:p-12"
              >
                <div className="font-heading text-5xl md:text-7xl text-ink leading-none tracking-tighter">
                  <Counter value={m.value} />
                </div>
                <p className="text-xs uppercase tracking-[0.22em] text-ink/60 mt-4 leading-snug">
                  {m.label}
                </p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Cosa ho fatto (only for Alajmo) */}
      {project.slug === "alajmo" && (
        <section
          id="section-cosa-ho-fatto"
          data-testid="alajmo-cosa-ho-fatto-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Cosa<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    ho fatto.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-ink/80 leading-relaxed text-lg">
                  Ho definito i <em className="text-peach not-italic font-medium">piani editoriali</em>, gestito il copywriting e la SEO content creation, prodotto reportage fotografici per i canali digital e le pubblicazioni editoriali, filmato e montato video che comunicassero i valori identitari del gruppo. Ho costruito e ottimizzato landing page e asset Shopify in coerenza con le brand guidelines.
                </p>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                  style={{
                    width: "100%",
                    backgroundColor: "#F2EFE9",
                    borderRadius: "8px",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/5e2ge37h_aljamo%20social.png"
                    alt="Alajmo — Mockup griglia Instagram su iPhone + MacBook"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "380px",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Progetti rappresentativi (only for Liquid Diamond) */}
      {project.slug === "liquid-diamond" && (
        <section
          id="section-progetti-rappresentativi"
          data-testid="liquid-diamond-rappresentativi"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Progetti rappresentativi</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl tracking-tighter leading-[0.9]">
                  Cinque<br />
                  <em className="text-peach text-3xl md:text-5xl">brand, cinque mestieri.</em>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="divide-y divide-line">
            {[
              {
                key: "vetekipp",
                title: "Vetekipp SRL",
                tag: "Brand positioning da zero",
                body:
                  "Gestione di mystery shopping e analisi sul campo, intervistando stakeholder e clienti per definire la strategia di marca. Elaborazione del posizionamento con definizione del Verbal Nail e Battlecry per la differenziazione sul mercato. Progettazione testi e layout per il sito web, sviluppo programma fedeltà, piani editoriali multicanale e supporto al piano di espansione commerciale.",
                img: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/fqsz57o9_vetekipp%20ld.png",
                alt: "Vetekipp — workshop strategico, documento, wireframe",
              },
              {
                key: "df-srl",
                title: "DF Srl",
                tag: "Manifatturiero B2B · Occhialeria · 2022 — 2024",
                body:
                  "Sviluppo della Guida alle Vendite: kit strategico con profilazione clienti, report degli incontri e flussi di gestione lead per ottimizzare il processo commerciale. Supporto vendite e reportage sul campo alle principali fiere internazionali (MIDO 2023). Piano editoriale con articoli e contenuti strategici.",
                img: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/ho0ibaha_df%20ld.png",
                alt: "DF Srl — Sales Toolkit, stand MIDO, sede",
              },
              {
                key: "euganea-vasi",
                title: "Euganea Vasi",
                tag: "B2B internazionale",
                body:
                  "Sviluppo contenuti per la rivista 'More Than a Pot': interviste e articoli di approfondimento con clientela internazionale per raccontare le storie dei brand. Social media management e pianificazione newsletter.",
                img: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/3847kcz0_euganea%20vasi%20ld.png",
                alt: "Euganea Vasi — Instagram + rivista More Than a Pot",
              },
              {
                key: "agua-quemada",
                title: "Agua Quemada",
                tag: "Ristorazione",
                body:
                  "Definizione landing page con messaggi strategici. Supporto alla creazione del brand e sviluppo del programma fedeltà 'AMIGOS' con automatismi WordPress-Mailchimp.",
                img: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/oyic7je9_agua%20quemada%20ld.png",
                alt: "Agua Quemada — mockup iPhone + landing AMIGOS",
              },
              {
                key: "pierre",
                title: "Pierre Trattoria Sartoriale",
                tag: "Fine dining",
                body:
                  "Sviluppo contenuti social con coordinamento del brand positioning. Supporto alla creazione dei messaggi di brand e redazione della carta dei valori.",
                img: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/vcz3njd3_pierre%20ld.png",
                alt: "Pierre Trattoria Sartoriale — griglia Instagram + mockup iPhone",
              },
            ].map((b, i) => {
              const reverse = (i + 1) % 2 === 0; // even blocks → image right, text left
              return (
                <Reveal key={b.key} delay={0.05}>
                  <div
                    data-testid={`ld-block-${b.key}`}
                    className={`py-12 md:py-16 grid grid-cols-12 gap-6 md:gap-12 items-center ${
                      reverse ? "md:[&>*:first-child]:order-2" : ""
                    }`}
                  >
                    <div className="col-span-12 md:col-span-5">
                      <div
                        className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                        style={{
                          width: "100%",
                          backgroundColor: "#F2EFE9",
                          borderRadius: "8px",
                        }}
                      >
                        <img
                          src={b.img}
                          alt={b.alt}
                          loading="lazy"
                          style={{
                            width: "100%",
                            height: "auto",
                            maxHeight: "280px",
                            display: "block",
                            objectFit: "contain",
                          }}
                        />
                      </div>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      <p className="text-xs uppercase tracking-[0.22em] text-peach mb-3">— {b.tag}</p>
                      <h3 className="font-heading text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-5">
                        {b.title}
                      </h3>
                      <p className="text-ink/80 leading-relaxed text-base md:text-lg">{b.body}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      {/* SPOTLIGHT — E-commerce & Contenuti (only for Atelier Biasetto) */}
      {project.slug === "atelier-biasetto" && (
        <section
          id="section-ecommerce-contenuti"
          data-testid="biasetto-ecommerce-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-20">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  E-commerce<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    & Contenuti.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          {/* Two-column layout: text + stats (left 55%) | shop screenshot (right 45%) */}
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-ink/80 leading-relaxed text-lg">
                  Ho diretto servizi fotografici e riprese video per l'e-commerce. Ho supervisionato i flussi di progettazione visiva, coordinando grafici e produzione creativa. Ho gestito la creazione di <span className="text-ink">macaron personalizzati</span>, dall'ideazione grafica alla stampa alimentare professionale. Ho ottimizzato gli asset Shopify e coordinato la strategia editoriale cross-canale.
                </p>
                <p className="text-ink/80 leading-relaxed text-lg mt-6">
                  Si è registrato un aumento del <em className="text-peach not-italic font-medium">2%</em> del volume delle vendite (+80 unità), con picchi significativi a dicembre e durante l'estate. Il fatturato annuo è aumentato del <em className="text-peach not-italic font-medium">14%</em>, con oltre la metà della crescita attribuibile a un prodotto di fascia alta.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-10 md:mt-12 grid grid-cols-2 gap-px bg-line max-w-[480px]">
                  {[
                    { value: "+14%", label: "fatturato e-commerce annuo" },
                    { value: "+8000", label: "follower in 14 mesi" },
                  ].map((m) => (
                    <div key={m.label} className="bg-cream p-6 md:p-8">
                      <div className="font-heading text-5xl md:text-6xl text-ink leading-none tracking-tighter">
                        <Counter value={m.value} />
                      </div>
                      <p className="text-xs uppercase tracking-[0.22em] text-ink/60 mt-3 leading-snug">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                  style={{ width: "100%", backgroundColor: "#F2EFE9", borderRadius: "8px" }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/3g4thbl3_Screenshot%202026-05-31%20alle%2016.44.14.png"
                    alt="Biasetto — Setteveli, unica e inimitabile (e-commerce screenshot)"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "380px",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Eventi (only for Atelier Biasetto) */}
      {project.slug === "atelier-biasetto" && (
        <section
          id="section-eventi"
          data-testid="biasetto-eventi-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Eventi.
                </h2>
              </Reveal>
            </div>
          </div>

          <Reveal delay={0.1}>
            <div className="max-w-4xl">
              <p className="text-ink/80 leading-relaxed text-lg">
                Ho coordinato la presenza istituzionale del Maestro in <em className="text-peach not-italic font-medium">4 eventi di settore</em> — dalla logistica alla produzione dei contenuti digitali. Fiere internazionali, panel, cerimonie: ogni appuntamento ha richiesto una narrazione coerente con il posizionamento del brand, prodotta prima, durante e dopo l'evento.
              </p>
            </div>
          </Reveal>

          <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              {
                src: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/0dfn7zne_Screenshot%202025-06-10%20alle%2021.22.39.png",
                alt: "Panel SIGEP — Sustainable District",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/bhxncjd4_Screenshot%202025-06-10%20alle%2021.26.45.png",
                alt: "Luigi Biasetto — Speech illy, tempo come ingrediente",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/862h97sy_Screenshot%202025-06-10%20alle%2021.39.45.png",
                alt: "Maestro d'Arte e Mestiere 2024 — cerimonia teatrale",
              },
              {
                src: "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/l4mu7uc6_Screenshot%202025-06-10%20alle%2021.54.25.png",
                alt: "Gruppo Maestri pasticcieri — ritratto istituzionale",
              },
            ].map((img, i) => (
              <Reveal key={img.alt} delay={0.1 + i * 0.07}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)] hover:shadow-[0_16px_40px_-16px_rgba(26,25,24,0.25)] transition-shadow duration-500"
                  style={{ width: "100%", backgroundColor: "#F2EFE9", borderRadius: "8px" }}
                >
                  <img
                    src={img.src}
                    alt={img.alt}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "240px",
                      display: "block",
                      objectFit: "cover",
                      objectPosition: "center",
                    }}
                  />
                </div>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Social Media Management (only for Atelier Biasetto) */}
      {project.slug === "atelier-biasetto" && (
        <section
          id="section-social-media-management"
          data-testid="biasetto-social-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Social Media<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    Management.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          {/* Two-column layout: text + stats (left 55%) | iPhone mockup (right 45%) */}
          <div className="grid grid-cols-12 gap-6 md:gap-12 items-start">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-ink/80 leading-relaxed text-lg">
                  Ho gestito la strategia social su due binari paralleli: il <em className="text-peach not-italic font-medium">personal brand</em> del Maestro Luigi Biasetto e la voce istituzionale della Pasticceria. Due toni, due strategie, un sistema coerente. Ho curato piano editoriale, produzione contenuti, direzione creativa e coordinamento con i grafici — costruendo una presenza digitale coerente con quattordici anni di Campionato del Mondo.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <ul className="mt-10 md:mt-12 space-y-3 border-t border-line pt-6 max-w-xl">
                  {[
                    { value: "73,8K", label: "follower profilo Luigi Biasetto" },
                    { value: "+8000", label: "follower in 14 mesi" },
                    { value: "1.614", label: "post pubblicati" },
                  ].map((s, i) => (
                    <li
                      key={s.label}
                      data-testid={`biasetto-social-stat-${i}`}
                      className="flex items-baseline gap-5"
                    >
                      <span className="font-heading text-3xl md:text-4xl text-ink tracking-tighter leading-none whitespace-nowrap min-w-[120px]">
                        {s.value}
                      </span>
                      <span className="text-xs uppercase tracking-[0.18em] text-ink/60">
                        {s.label}
                      </span>
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)] mx-auto md:mx-0"
                  style={{
                    width: "100%",
                    maxWidth: "320px",
                    backgroundColor: "#000000",
                    borderRadius: "8px",
                  }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/n1e2xe4v_IMG_8421-portrait.png"
                    alt="Mockup Instagram — profilo Luigi Biasetto"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "480px",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}
      {project.slug === "uqido" && (
        <section
          id="section-chatdoc"
          data-testid="uqido-chatdoc-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-20">
            <div className="col-span-12 md:col-span-8">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  ChatDoc<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    5 prodotti, un'architettura narrativa.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <Reveal className="col-span-12 md:col-span-7" delay={0.1}>
              <p className="text-ink/80 leading-relaxed text-lg">
                ChatDoc non è un chatbot. È un'infrastruttura di sicurezza per la conoscenza aziendale — progettata per non allucinare, per citare le fonti, per restare dentro i confini dell'azienda. Il lavoro comunicativo è stato costruire questo posizionamento partendo dal <em className="text-peach not-italic font-medium">perché</em>, prima ancora di decidere cosa comunicare sui social.
              </p>
              <p className="text-ink/80 leading-relaxed text-lg mt-6">
                Ho pianificato l'analisi di mercato, con benchmark e riferimenti, per poi definire il brand positioning delle 5 varianti di prodotto — <span className="text-ink">On the Ground, Operation, R&amp;D, Office, Quote</span> — ognuna con un pain point preciso del manifatturiero italiano.
              </p>
              <p className="text-ink/80 leading-relaxed text-lg mt-6">
                Ho scritto il copy ADV per Meta e Google, coordinato la produzione visiva dei contenuti, creato le landing pages e costruito il funnel di marketing e lead generation.
              </p>
            </Reveal>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="rounded-2xl overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)] hover:shadow-[0_16px_40px_-16px_rgba(26,25,24,0.25)] transition-shadow duration-500 flex items-center justify-center"
                  style={{ backgroundColor: "#F2EFE9" }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/vt1jro29_Screenshot%202026-05-31%20alle%2015.40.05.png"
                    alt="ChatDoc — La tua azienda sa già tutto"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "420px",
                      objectFit: "contain",
                      display: "block",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>

          {/* Product links */}
          <div className="mt-16 md:mt-20 border-t border-line pt-10">
            <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-8">— Guarda i prodotti</p>
            <div className="flex flex-wrap gap-x-10 gap-y-4">
              {[
                { label: "ChatDoc Operational", href: "https://chatdoc.uqido.com/operational" },
                { label: "ChatDoc On the Ground", href: "https://chatdoc.uqido.com/on-the-ground" },
                { label: "ChatDoc Office", href: "https://chatdoc.uqido.com/office" },
              ].map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`chatdoc-product-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-peach hover:text-peach-hover transition-colors"
                >
                  {l.label}
                  <span className="inline-block transition-transform duration-300 group-hover:translate-x-1">→</span>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Beyond the Hype (only for UQIDO) */}
      {project.slug === "uqido" && (
        <section
          id="section-beyond-the-hype"
          data-testid="uqido-beyond-the-hype-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-20">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Beyond the Hype.<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    L'AI che produce valore nell'industria.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          {/* Two-column layout: text + stats (left 55%) | speakers image (right 45%) */}
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-ink/80 leading-relaxed text-lg">
                  65+ C-level in presenza, speaker da BCG Platinion, LinkedIn Top Voice, CEO di aziende manifatturiere. Un format ibrido che univa masterclass, formazione e networking qualificato. Ho gestito le campagne di lead generation digitali, con campagne ADV su META e ADS su Google. Ho elaborato, oltre al concept del format, la comunicazione pre-evento multicanale, landing page di conversione, coordinamento speaker e contenuti, produzione dei materiali di sala e content post-evento per amplificazione organica.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-10 md:mt-12 grid grid-cols-2 gap-px bg-line max-w-[480px]">
                  {[
                    { value: "65+", label: "C-level ospiti" },
                    { value: "9", label: "Speaker e conduttori" },
                  ].map((m) => (
                    <div key={m.label} className="bg-cream p-6 md:p-8">
                      <div className="font-heading text-5xl md:text-6xl text-ink leading-none tracking-tighter">
                        <Counter value={m.value} />
                      </div>
                      <p className="text-xs uppercase tracking-[0.22em] text-ink/60 mt-3 leading-snug">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                  style={{ width: "100%", backgroundColor: "#1A1918", borderRadius: "8px" }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/3ik98md2_uqido%20-%20Slide%20beyond%20the%20hype_2.png"
                    alt="Beyond the Hype — 9 speaker dell'evento"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "320px",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Masterclass AI for Business (only for UQIDO) */}
      {project.slug === "uqido" && (
        <section
          id="section-masterclass-ai-for-business"
          data-testid="uqido-masterclass-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-16 md:mb-20">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Masterclass<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    AI for Business.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          {/* Two-column layout: text + stats (left 55%) | masterclass photo (right 45%) */}
          <div className="grid grid-cols-12 gap-6 md:gap-12">
            <div className="col-span-12 md:col-span-7">
              <Reveal delay={0.1}>
                <p className="text-ink/80 leading-relaxed text-lg">
                  Format mensile di formazione per imprenditori e C-level — da novembre 2025 a maggio 2026, con focus su AI applicata ai processi industriali. Programma di certificazione ufficiale <span className="text-ink">"Process Innovation Architect"</span>. Ho gestito concept, comunicazione, landing page, coordinamento contenuti e speaker, lead acquisition attraverso canali diretti e indiretti.
                </p>
              </Reveal>

              <Reveal delay={0.2}>
                <div className="mt-10 md:mt-12 grid grid-cols-2 gap-px bg-line max-w-[480px]">
                  {[
                    { value: "70+", label: "C-level alle Masterclass" },
                    { value: "7", label: "Edizioni in 7 mesi" },
                  ].map((m) => (
                    <div key={m.label} className="bg-cream p-6 md:p-8">
                      <div className="font-heading text-5xl md:text-6xl text-ink leading-none tracking-tighter">
                        <Counter value={m.value} />
                      </div>
                      <p className="text-xs uppercase tracking-[0.22em] text-ink/60 mt-3 leading-snug">
                        {m.label}
                      </p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="col-span-12 md:col-span-5">
              <Reveal delay={0.15}>
                <div
                  className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                  style={{ width: "100%", backgroundColor: "#F2EFE9", borderRadius: "8px" }}
                >
                  <img
                    src="https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/2easqfpc_IMG_2402%20%282%29.JPG"
                    alt="Masterclass AI for Business — workshop in aula UQIDO"
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "auto",
                      maxHeight: "380px",
                      display: "block",
                      objectFit: "contain",
                    }}
                  />
                </div>
              </Reveal>
            </div>
          </div>
        </section>
      )}

      {/* SPOTLIGHT — Personal Branding LinkedIn (only for UQIDO) */}
      {project.slug === "uqido" && (
        <section
          id="section-personal-branding"
          data-testid="uqido-personal-branding-spotlight"
          className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16 scroll-mt-28"
        >
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-10">— Spotlight</p>
          <div className="grid grid-cols-12 gap-6 md:gap-10 items-end mb-12 md:mb-16">
            <div className="col-span-12 md:col-span-9">
              <Reveal>
                <h2 className="font-heading text-5xl md:text-7xl lg:text-8xl tracking-tighter leading-[0.9]">
                  Personal Branding<br />
                  <em className="text-peach text-3xl md:text-5xl lg:text-6xl">
                    — LinkedIn.
                  </em>
                </h2>
              </Reveal>
            </div>
          </div>

          {/* Intro */}
          <Reveal delay={0.1}>
            <div className="max-w-4xl">
              <p className="text-ink/80 leading-relaxed text-lg">
                Ho costruito due personal brand distinti su LinkedIn per figure chiave di Uqido. Non ghostwriting puro: <em className="text-peach not-italic font-medium">architettura identitaria</em>. Per ciascuno ho definito posizionamento, struttura editoriale, formato dei contenuti e tono di voce — poi ho scritto e prodotto i contenuti in autonomia.
              </p>
            </div>
          </Reveal>

          {/* Two profiles 50/50 */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16">
            {[
              {
                key: "cristiano",
                name: "Cristiano Gerunda",
                role: "AI Business Unit Director @ Uqido",
                positioning:
                  "Posizionamento: voce italiana sull'AI industriale applicata — non teorica, non entusiasta acritica.",
                stats: [
                  { value: "362→1069", label: "follower (feb–mag 2026)" },
                  { value: "+195%", label: "crescita in 4 mesi" },
                  { value: "850+", label: "impression settimanali" },
                ],
                img:
                  "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/i00n26ne_img_ai_PB%20Cristiano.png",
                alt: "Cristiano Gerunda — Post LinkedIn 'Chaos × AI = Faster Chaos'",
              },
              {
                key: "gianpaolo",
                name: "Gianpaolo Greco",
                role: "XR Business Unit Director @ Uqido",
                positioning:
                  "Posizionamento: il manager che ragiona sui sistemi, non sui tool.",
                stats: [
                  { value: "1891→2076", label: "follower (feb–mag 2026)" },
                  { value: "+10%", label: "crescita su base esistente" },
                  { value: "685+", label: "impression settimanali" },
                ],
                img:
                  "https://customer-assets.emergentagent.com/job_visual-engine-20/artifacts/eged9b9n_Gemini_Generated_Image_qi32avqi32avqi32.png",
                alt: "Gianpaolo Greco — Post LinkedIn 'L'inverno dell'XR è arrivato'",
              },
            ].map((p, idx) => (
              <Reveal key={p.key} delay={0.15 + idx * 0.1}>
                <article className="flex flex-col">
                  <h3 className="font-heading text-3xl md:text-4xl tracking-tight leading-tight">
                    {p.name}
                  </h3>
                  <p className="text-xs uppercase tracking-[0.22em] text-peach mt-2">{p.role}</p>
                  <p className="mt-6 text-ink/80 leading-relaxed text-base md:text-lg">
                    {p.positioning}
                  </p>

                  <ul className="mt-8 space-y-3 border-t border-line pt-6">
                    {p.stats.map((s, i) => (
                      <li
                        key={s.label}
                        data-testid={`pb-${p.key}-stat-${i}`}
                        className="flex items-baseline gap-4"
                      >
                        <span className="font-heading text-2xl md:text-3xl text-ink tracking-tighter leading-none whitespace-nowrap">
                          {s.value}
                        </span>
                        <span className="text-xs uppercase tracking-[0.18em] text-ink/60">
                          {s.label}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-10">
                    <div
                      className="overflow-hidden shadow-[0_8px_24px_-12px_rgba(26,25,24,0.18)]"
                      style={{
                        width: "100%",
                        maxWidth: "320px",
                        backgroundColor: "#1A1918",
                        borderRadius: "8px",
                      }}
                    >
                      <img
                        src={p.img}
                        alt={p.alt}
                        loading="lazy"
                        style={{
                          width: "100%",
                          maxWidth: "320px",
                          height: "auto",
                          display: "block",
                          objectFit: "contain",
                        }}
                      />
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      {/* SERVICES */}
      {(project.tags?.length || project.services?.length) > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16">
          <div className="grid grid-cols-12 gap-6">
            <div className="col-span-12 md:col-span-4">
              <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-4">— Servizi erogati</p>
              <h2 className="font-heading text-4xl md:text-5xl tracking-tight">Cosa ho fatto.</h2>
            </div>
            <div className="col-span-12 md:col-span-8 flex flex-wrap gap-3 self-end">
              {[...(project.tags || []), ...(project.services || [])].map((s, i) => (
                <span
                  key={`${s}-${i}`}
                  className="border border-line-dark rounded-full px-5 py-2.5 text-sm text-ink/80"
                >
                  {s}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SUB PROJECTS */}
      {project.sub_projects?.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-24 md:mt-32 border-t border-line pt-16">
          <p className="text-xs uppercase tracking-[0.3em] text-ink/60 mb-12">— Dettaglio interventi</p>
          <div className="space-y-0">
            {project.sub_projects.map((sp, i) => {
              const slug = sp.title
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "");
              // Avoid duplicate id collision with the UQIDO spotlight (#section-chatdoc).
              // Anchor nav still points sub-projects via #section-<slug>, ChatDoc spotlight
              // owns "section-chatdoc"; this sub-block uses a distinct id.
              const anchor =
                project.slug === "uqido" &&
                ["chatdoc", "beyond-the-hype", "masterclass-ai-for-business", "personal-branding"].includes(slug)
                  ? `subsection-${slug}`
                  : `section-${slug}`;
              return (
                <Reveal key={sp.title} delay={i * 0.08}>
                  <div
                    id={anchor}
                    className="grid grid-cols-12 gap-6 py-10 border-t border-line scroll-mt-28"
                  >
                    <div className="col-span-12 md:col-span-1">
                      <span className="num-index text-2xl md:text-3xl text-peach">
                        {String(i + 1).padStart(2, "0")}
                      </span>
                    </div>
                    <div className="col-span-12 md:col-span-4">
                      <h3 className="font-heading text-3xl md:text-4xl tracking-tight">{sp.title}</h3>
                    </div>
                    <div className="col-span-12 md:col-span-7">
                      <p className="text-ink/80 leading-relaxed text-lg">{sp.description}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 mt-32">
        <Link
          to="/contatti"
          className="inline-flex items-center gap-3 bg-peach text-cream rounded-full px-8 py-5 text-xs uppercase tracking-[0.22em] hover:bg-peach-hover transition-colors"
        >
          Lavoriamo insieme <ArrowUpRight size={16} />
        </Link>
      </section>
    </div>
  );
}
